// CareerDetailPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Breadcrumb from '../components/Breadcrumb';

const CareerDetailPage = () => {
  const { jobSlug } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    portfolio: '',
    resume: null,
    coverLetter: '',
    heardFrom: '',
    startDate: '',
    salaryExpectation: '',
    additionalInfo: ''
  });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    // Mock job data
    const jobsData = {
      "senior-investment-associate": {
        id: 1,
        slug: "senior-investment-associate",
        title: "Senior Investment Associate",
        department: "Investment",
        location: "New York",
        type: "Full-time",
        experience: "5-8 years",
        description: "We're seeking a Senior Investment Associate to join our growing team. You will play a key role in deal sourcing, due diligence, and portfolio management.",
        responsibilities: [
          "Lead due diligence processes for potential investments",
          "Develop financial models and investment memos",
          "Monitor portfolio company performance",
          "Support senior partners in deal execution",
          "Mentor junior team members",
          "Build relationships with management teams"
        ],
        requirements: [
          "5-8 years of experience in investment banking or private equity",
          "Strong financial modeling and analytical skills",
          "Excellent communication and presentation abilities",
          "MBA or relevant advanced degree preferred",
          "Proven track record of successful deal execution"
        ],
        benefits: [
          "Competitive base salary + performance bonus",
          "Health, dental, and vision insurance",
          "401(k) matching",
          "Professional development budget",
          "Global mobility opportunities"
        ]
      },
      "financial-analyst": {
        id: 2,
        slug: "financial-analyst",
        title: "Financial Analyst",
        department: "Advisory",
        location: "New York",
        type: "Full-time",
        experience: "2-4 years",
        description: "Join our advisory practice as a Financial Analyst, supporting clients with financial analysis, modeling, and strategic recommendations.",
        responsibilities: [
          "Build and maintain complex financial models",
          "Conduct market research and competitive analysis",
          "Prepare client presentations and reports",
          "Support senior advisors on client engagements"
        ],
        requirements: [
          "2-4 years of experience in finance or consulting",
          "Proficiency in Excel and financial modeling",
          "Strong analytical and problem-solving skills",
          "Bachelor's degree in Finance, Economics, or related field"
        ],
        benefits: [
          "Competitive compensation package",
          "Comprehensive healthcare",
          "Learning & development stipend",
          "Flexible work arrangements"
        ]
      },
      "operations-manager": {
        id: 3,
        slug: "operations-manager",
        title: "Operations Manager",
        department: "Operations",
        location: "London",
        type: "Full-time",
        experience: "6-10 years",
        description: "We're looking for an experienced Operations Manager to optimize our internal processes and support portfolio company operations.",
        responsibilities: [
          "Streamline internal workflows and systems",
          "Lead operational improvement initiatives",
          "Manage vendor relationships and contracts",
          "Coordinate cross-functional projects"
        ],
        requirements: [
          "6-10 years of operations management experience",
          "Strong project management and leadership skills",
          "Experience in professional services or finance preferred",
          "PMP certification is a plus"
        ],
        benefits: [
          "Executive benefits package",
          "Performance bonus",
          "Pension scheme",
          "Private healthcare"
        ]
      }
    };

    // Simulate API call
    setTimeout(() => {
      const jobData = jobsData[jobSlug];
      if (jobData) {
        setJob(jobData);
      }
      setLoading(false);
    }, 500);
  }, [jobSlug]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'resume' && files) {
      setFormData({ ...formData, resume: files[0] });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("Application submitted:", formData, "for job:", job);
    setSubmitted(true);
    // Here you would send data to your backend
    setTimeout(() => {
      navigate('/careers');
    }, 3000);
  };

  if (loading) {
    return (
      <div className="career-detail-loading">
        <div className="museum-wrap">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="career-detail-not-found">
        <div className="museum-wrap">
          <h2>Position Not Found</h2>
          <p>The job you're looking for doesn't exist or has been filled.</p>
          <Link to="/careers" className="back-to-careers-btn">View All Positions </Link>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="career-detail-submitted">
        <div className="museum-wrap">
          <div className="submitted-content">
            <div className="success-icon">✓</div>
            <h2>Application Submitted!</h2>
            <p>Thank you for applying for the <strong>{job.title}</strong> position.</p>
            <p>Our recruiting team will review your application and contact you within 5-7 business days.</p>
            <Link to="/careers" className="back-to-careers-btn">View Other Positions </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Breadcrumb />
      <div className="career-detail-page">
        <div className="museum-wrap">
          <Link to="/careers" className="back-to-list">
            ← Back to All Positions
          </Link>

          <div className="career-detail-grid">
            {/* Left Column - Job Details */}
            <div className="job-details">
              <div className="job-header">
                <h1>{job.title}</h1>
                <div className="job-meta">
                  <span className="job-meta-item">{job.department}</span>
                  <span className="job-meta-item">{job.location}</span>
                  <span className="job-meta-item">{job.type}</span>
                  <span className="job-meta-item">{job.experience}</span>
                </div>
              </div>

              <div className="job-section">
                <h2>About the Role</h2>
                <p>{job.description}</p>
              </div>

              <div className="job-section">
                <h2>Key Responsibilities</h2>
                <ul className="job-list">
                  {job.responsibilities.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>

              <div className="job-section">
                <h2>Qualifications & Requirements</h2>
                <ul className="job-list">
                  {job.requirements.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>

              <div className="job-section">
                <h2>What We Offer</h2>
                <ul className="job-list">
                  {job.benefits.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Right Column - Application Form (Styled like Contact Form) */}
            <div className="application-form-wrapper">
              <div className="application-form-card">
                <h3 className="form-title">Apply for this position</h3>
                <p className="form-subtitle">
                </p>

                <form onSubmit={handleSubmit}>
                  <div className="form-row">
                    <div className="form-group">
                      <label>First Name *</label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Last Name *</label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Phone Number</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Portfolio / LinkedIn URL</label>
                    <input
                      type="url"
                      name="portfolio"
                      placeholder="https://linkedin.com/in/yourprofile"
                      value={formData.portfolio}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-group">
                    <label>Resume/CV *</label>
                    <input
                      type="file"
                      name="resume"
                      accept=".pdf,.doc,.docx"
                      onChange={handleChange}
                      required
                    />
                    <small>Accepted formats: PDF, DOC, DOCX (Max 5MB)</small>
                  </div>

                  <div className="form-group">
                    <label>Cover Letter</label>
                    <textarea
                      name="coverLetter"
                      rows="5"
                      placeholder="Tell us why you're interested in this position and what makes you a great fit..."
                      value={formData.coverLetter}
                      onChange={handleChange}
                    ></textarea>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>How did you hear about us?</label>
                      <select name="heardFrom" value={formData.heardFrom} onChange={handleChange}>
                        <option value="">Select an option</option>
                        <option value="linkedin">LinkedIn</option>
                        <option value="referral">Employee Referral</option>
                        <option value="job-board">Job Board</option>
                        <option value="career-site">Morphis Careers</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Earliest Start Date</label>
                      <input
                        type="text"
                        name="startDate"
                        placeholder="e.g., Immediate, 2 weeks, January 2025"
                        value={formData.startDate}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Salary Expectation (Annual)</label>
                    <input
                      type="text"
                      name="salaryExpectation"
                      placeholder="e.g., $120,000 - $150,000"
                      value={formData.salaryExpectation}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-group">
                    <label>Additional Information</label>
                    <textarea
                      name="additionalInfo"
                      rows="3"
                      placeholder="Any other information you'd like to share with us..."
                      value={formData.additionalInfo}
                      onChange={handleChange}
                    ></textarea>
                  </div>

                  <button type="submit" className="submit-btn">
                    Submit Application <i className="fa fa-arrow-right"></i>
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        /* Base styles for career detail page */
        .career-detail-page {
          background: #fffdf6;
          min-height: 100vh;
          padding: 40px 0 80px;
        }

        .back-to-list {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-family: var(--font-body, Arial, sans-serif);
          font-size: 13px;
          color: var(--text-muted, #6b7280);
          text-decoration: none;
          margin-bottom: 40px;
          transition: color 0.2s;
        }

        .back-to-list:hover {
          color: var(--color-red, #E87722);
        }

        .career-detail-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 60px;
        }

        /* Job Details Styles */
        .job-header {
          margin-bottom: 32px;
        }

        .job-header h1 {
          font-family: var(--font-heading, 'IBM Plex Serif', serif);
          font-size: clamp(1.8rem, 4vw, 2.5rem);
          margin-bottom: 20px;
          color: var(--color-dark, #1a1a1a);
        }

        .job-meta {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
        }

        .job-meta-item {
          font-family: var(--font-body, Arial, sans-serif);
          font-size: 12px;
          padding: 6px 14px;
          background: #ffe9c34f;
          color: var(--color-red, #E87722);
          border-radius: 20px;
        }

        .job-section {
          margin-bottom: 40px;
        }

        .job-section h2 {
          font-family: var(--font-heading, 'IBM Plex Serif', serif);
          font-size: 1.4rem;
          margin-bottom: 20px;
          color: var(--color-dark, #1a1a1a);
        }

        .job-section p {
          font-family: var(--font-body, Arial, sans-serif);
          font-size: 1rem;
          line-height: 1.7;
          color: #2d2d2d;
        }

        .job-list {
          list-style: none;
          padding: 0;
        }

        .job-list li {
          font-family: var(--font-body, Arial, sans-serif);
          font-size: 0.95rem;
          line-height: 1.6;
          color: #2d2d2d;
          margin-bottom: 12px;
          padding-left: 24px;
          position: relative;
        }

        

        /* Application Form - Styled like Contact Form */
        .application-form-wrapper {
          position: sticky;
          top: 100px;
        }

        .application-form-card {
          background: #fffdf6;
          border: 1px solid var(--border-light, #e6e0d4);
          padding: 48px;
          position: relative;
          transition: all 0.3s ease;
        }

        .application-form-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 4px;
          height: 100%;
        }

        .form-title {
          font-family: var(--font-heading, 'IBM Plex Serif', serif);
          font-size: 28px;
          font-weight: 700;
          margin-bottom: 8px;
          color: var(--color-dark, #1a1a1a);
        }

        .form-subtitle {
          font-size: 15px;
          color: var(--text-muted, #6b7280);
          margin-bottom: 32px;
          line-height: 1.5;
        }

        .form-subtitle strong {
          color: var(--color-red, #E87722);
        }

        .form-group {
          margin-bottom: 28px;
        }

        .form-group label {
          display: block;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: var(--text-muted, #6b7280);
          margin-bottom: 10px;
        }

        .form-group input,
        .form-group select,
        .form-group textarea {
          width: 100%;
          padding: 12px 0;
          font-family: var(--font-body, Arial, sans-serif);
          font-size: 15px;
          color: var(--color-dark, #1a1a1a);
          background: transparent;
          border: none;
          border-bottom: 1.5px solid var(--border-light, #e6e0d4);
          outline: none;
          transition: border-color 0.2s ease;
        }

        .form-group input:focus,
        .form-group select:focus,
        .form-group textarea:focus {
          border-bottom-color: var(--color-red, #E87722);
        }

        .form-group input::placeholder,
        .form-group textarea::placeholder {
          color: #c0c0c0;
          font-style: italic;
        }

        .form-group input[type="file"] {
          padding: 8px 0;
          border: none;
          border-bottom: 1.5px solid var(--border-light, #e6e0d4);
          font-size: 13px;
        }

        .form-group small {
          font-family: var(--font-body, Arial, sans-serif);
          font-size: 10px;
          color: var(--text-muted, #6b7280);
          display: block;
          margin-top: 8px;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 28px;
          margin-bottom: 0;
        }

        .form-group textarea {
          resize: none;
          min-height: 110px;
        }

        .submit-btn {
          width: 100%;
          padding: 16px 28px;
          background: var(--color-dark, #1a1a1a);
          color: #FFFDF6;
          font-family: var(--font-body, Arial, sans-serif);
          font-size: 13px;
          font-weight: 600;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          border: none;
          cursor: pointer;
          transition: all 0.3s ease;
          margin-top: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
        }

        .submit-btn:hover {
          background: var(--color-red, #E87722);
          transform: translateY(-2px);
        }

        .submit-btn i {
          transition: transform 0.3s ease;
        }

        .submit-btn:hover i {
          transform: translateX(5px);
        }

        /* Loading State */
        .career-detail-loading {
          min-height: 60vh;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .loading-spinner {
          width: 50px;
          height: 50px;
          border: 3px solid var(--border-light, #e6e0d4);
          border-top-color: var(--color-red, #E87722);
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* Not Found */
        .career-detail-not-found {
          min-height: 60vh;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
        }

        .career-detail-not-found h2 {
          font-family: var(--font-heading, 'IBM Plex Serif', serif);
          font-size: 2rem;
          margin-bottom: 16px;
        }

        .career-detail-not-found p {
          font-family: var(--font-body, Arial, sans-serif);
          margin-bottom: 24px;
        }

        .back-to-careers-btn {
          display: inline-block;
          padding: 12px 28px;
          background: var(--color-red, #E87722);
          color: #fff;
          text-decoration: none;
          font-family: var(--font-body, Arial, sans-serif);
          font-size: 13px;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }

        /* Submitted State */
        .career-detail-submitted {
          min-height: 70vh;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .submitted-content {
          text-align: center;
          max-width: 500px;
          margin: 0 auto;
        }

        .success-icon {
          width: 80px;
          height: 80px;
          background: #4CAF50;
          color: #fff;
          font-size: 48px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 24px;
        }

        .submitted-content h2 {
          font-family: var(--font-heading, 'IBM Plex Serif', serif);
          font-size: 1.8rem;
          margin-bottom: 16px;
        }

        .submitted-content p {
          font-family: var(--font-body, Arial, sans-serif);
          color: var(--text-muted, #6b7280);
          margin-bottom: 16px;
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .career-detail-grid {
            grid-template-columns: 1fr;
            gap: 50px;
          }

          .application-form-wrapper {
            position: static;
          }
        }

        @media (max-width: 768px) {
          .career-detail-page {
            padding: 20px 0 60px;
          }

          .application-form-card {
            padding: 32px 24px;
          }

          .form-row {
            grid-template-columns: 1fr;
            gap: 0;
          }

          .form-title {
            font-size: 24px;
          }

          .submit-btn {
            padding: 14px 24px;
          }
        }

        @media (min-width: 769px) and (max-width: 1024px) {
          .application-form-card {
            padding: 40px 32px;
          }
        }
      `}</style>
    </>
  );
};

export default CareerDetailPage;